import {
  ActivityEntity,
  AccountActivityType,
  AccountActivitySubtype,
  AccountActivityOutcome,
  SessionAuthenticationMethod,
  SessionMfaMethod
} from './types';

// ═══════════════════════════════════════════════════════════════════════════
// Outcome Styling Configuration
// ═══════════════════════════════════════════════════════════════════════════

export interface OutcomeConfig {
  color: string;
  backgroundColor: string;
  icon: string;
  label: string;
}

export const outcomeConfig: Record<AccountActivityOutcome, OutcomeConfig> = {
  success: {
    color: 'var(--gr-green)',
    backgroundColor: 'rgba(86, 182, 139, 0.1)',
    icon: 'check-circle',
    label: 'Success',
  },
  failed: {
    color: 'var(--gr-red)',
    backgroundColor: 'rgba(219, 48, 48, 0.1)',
    icon: 'x-circle',
    label: 'Failed',
  },
  blocked: {
    color: 'var(--gr-light-orange)',
    backgroundColor: 'rgba(232, 137, 29, 0.1)',
    icon: 'alert-triangle',
    label: 'Blocked',
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// Activity Type to Action Verb Mapping
// ═══════════════════════════════════════════════════════════════════════════

export const activityTypeVerbs: Record<AccountActivityType, string> = {
  login: 'logged in',
  create: 'created',
  update: 'updated',
  delete: 'deleted',
  export: 'exported',
  search: 'searched',
  read: 'viewed',
};

// Subtype can override the verb
export const activitySubtypeVerbs: Partial<Record<AccountActivitySubtype, string>> = {
  // Login subtypes
  password: 'logged in with password',
  passwordless: 'logged in passwordless',
  facebook: 'logged in with Facebook',
  refresh: 'refreshed session',
  activation: 'activated a new session',
  impersonationStart: 'started impersonating',
  impersonationEnd: 'stopped impersonating',
  mfaVerification: 'verified MFA',
  contextSelection: 'selected context',
  // Financial subtypes
  refund: 'refunded',
  cancel: 'cancelled',
  payment: 'processed payment for',
  dispute: 'disputed',
  // Specialized operation subtypes
  reply: 'replied to',
  checkIn: 'checked in',
  transfer: 'transferred',
  clone: 'cloned',
  statusChange: 'changed status of',
  reactivate: 'reactivated',
  urlChange: 'changed URL of',
  franchiseChange: 'changed franchise of',
  mfaEnforcementChange: 'updated MFA enforcement for',
  reportedAsSpam: 'archived and flagged (as spam)',
  mfaReset: 'reset MFA for',
  // Credential subtypes. Hardcoded English like the rest of this map, per the audit-trail skill.
  passwordChange: 'changed the password for',
  passwordResetForced: 'forced a password reset for',
  passwordResetSatisfied: 'set a new password for',
  systemPromotion: 'promoted to system admin',
  mfaEnrollment: 'enrolled in MFA',
  mfaUnenrollment: 'removed MFA from',
  mfaSecretIssued: 'issued a new MFA secret for',
  // Passkey subtypes
  passkey: 'logged in with a passkey',
  passkeyAdded: 'added a passkey to',
  passkeyRemoved: 'removed a passkey from',
  passkeyRenamed: 'renamed a passkey on'
};

// How the acting session signed in, for the badge on each row. Hardcoded English like the verbs.
export const signInStartPhrases: Record<SessionAuthenticationMethod, string> = {
  password: 'a password',
  passkey: 'a passkey',
  activation: 'a link',
  facebook: 'Facebook',
  refresh: 'a refresh'
};

export const signInStartLabels: Record<SessionAuthenticationMethod, string> = {
  password: 'Password',
  passkey: 'Passkey',
  activation: 'Link',
  facebook: 'Facebook',
  refresh: 'Refresh'
};

export const mfaMethodPhrases: Record<SessionMfaMethod, string> = {
  otp: 'a code',
  passkey: 'a passkey',
  recoveryCode: 'a recovery code'
};

export const mfaMethodLabels: Record<SessionMfaMethod, string> = {
  otp: 'code',
  passkey: 'passkey',
  recoveryCode: 'recovery code'
};

// ═══════════════════════════════════════════════════════════════════════════
// Target Entity Route Mapping
// ═══════════════════════════════════════════════════════════════════════════

export interface TargetEntityConfig {
  getDescription: (name: string | undefined, id?: number) => string;
  getLink: (id: number, extraData?: Record<string, unknown>) => string | null;
}

export const targetEntityConfig: Record<ActivityEntity, TargetEntityConfig> = {
  account: {
    getDescription: (name) => `account "${name || 'Unknown'}"`,
    getLink: (id) => `/accounts/detail/${id}/profile`,
  },
  organization: {
    getDescription: (name) => `organization "${name || 'Unknown'}"`,
    getLink: (id) => `/organizations/edit/${id}/about`,
  },
  accessKey: {
    getDescription: (name) => `access key "${name || 'Unknown'}"`,
    getLink: () => null, // No direct view, list only
  },
  campaign: {
    getDescription: (name) => `campaign "${name || 'Unknown'}"`,
    getLink: (id) => `/campaigns/performance/${id}`,
  },
  event: {
    getDescription: (name) => `event "${name || 'Unknown'}"`,
    getLink: (id) => `/events/performance/${id}`,
  },
  fundraiser: {
    getDescription: (name) => `fundraiser "${name || 'Unknown'}"`,
    getLink: (id) => `/fundraisers/view/${id}`,
  },
  team: {
    getDescription: (name) => `team "${name || 'Unknown'}"`,
    getLink: (id) => `/fundraisers/team/view/${id}`,
  },
  collection: {
    getDescription: (name) => `collection "${name || 'Unknown'}"`,
    getLink: (id) => `/fundraisers/collection/view/${id}`,
  },
  donation: {
    getDescription: (name, id) => `donation #${id || 'Unknown'}`,
    getLink: (id) => `/donations/view/${id}`,
  },
  donationSubscription: {
    getDescription: (name, id) => `recurring donation #${id || 'Unknown'}`,
    getLink: (id) => `/recurring/detail/${id}/donations`,
  },
  sale: {
    getDescription: (name, id) => `sale #${id || 'Unknown'}`,
    getLink: (id) => `/sales/view/${id}`,
  },
  saleDetail: {
    getDescription: (name) => `sale item "${name || 'Unknown'}"`,
    getLink: (id, extraData) => {
      const saleId = extraData?.saleId as number | undefined;
      return saleId ? `/sales/view/${saleId}` : null;
    },
  },
  ticket: {
    getDescription: (name) => `ticket "${name || 'Unknown'}"`,
    getLink: () => null, // No direct route
  },
  merchandise: {
    getDescription: (name) => `merchandise "${name || 'Unknown'}"`,
    getLink: () => null, // No direct route
  },
  series: {
    getDescription: (name) => `series "${name || 'Unknown'}"`,
    getLink: () => null, // No direct route
  },
  franchise: {
    getDescription: (name) => `franchise "${name || 'Unknown'}"`,
    getLink: (id) => `/system-settings/franchises/edit/${id}`,
  },
  campaignAmbassadorProgram: {
    getDescription: (name) => `campaign PlaceHub program "${name || 'Unknown'}"`,
    getLink: () => null, // Awaiting API changes
  },
  eventAmbassadorProgram: {
    getDescription: (name) => `event PlaceHub program "${name || 'Unknown'}"`,
    getLink: () => null, // Awaiting API changes
  },
  eventAmbassador: {
    getDescription: (name) => `ambassador "${name || 'Unknown'}"`,
    getLink: (id) => `/vip-program/vip/${id}/part-one`,
  },
  emailInstance: {
    getDescription: (name) => `email "${name || 'Unknown'}"`,
    getLink: () => null, // No direct view
  },
  saleCoupon: {
    getDescription: (name) => `coupon`,
    getLink: () => null, // No direct view
  },
  page: {
    getDescription: (name) => `fundraising pages`,
    getLink: (id) => `/pages/edit/${id}`,
  },
  emailCustomSetting: {
    getDescription: (name) => `email custom setting`,
    getLink: () => null, // No direct view
  },
  terminal: {
    getDescription: (name) => `terminal`,
    getLink: (id) => null, // No direct view
  },
  emailLog: {
    getDescription: () => `email journey summary`,
    getLink: () => null, // Export-only entity
  },
  integration: {
    getDescription: () => `an organization integration`,
    getLink: () => null,
  },
  report: {
    getDescription: () => `a report`,
    getLink: () => null, // Export-only entity
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// i18n Keys
// ═══════════════════════════════════════════════════════════════════════════

export const i18nKeys = {
  TITLE: 'systemSettings.auditTrail.title',
  NO_DATA: 'systemSettings.auditTrail.noData',
  ERROR_LOADING: 'systemSettings.auditTrail.errorLoading',
  LOAD_MORE: 'systemSettings.auditTrail.loadMore',
  ACTING_AS: 'systemSettings.auditTrail.actingAs',
  IMPERSONATED_BY: 'systemSettings.auditTrail.impersonatedBy',
  ADDITIONAL_DATA: 'systemSettings.auditTrail.additionalData',
  VIA: 'systemSettings.auditTrail.via',
  ACCESS_KEY: 'systemSettings.auditTrail.accessKey',
  MFA_VERIFIED: 'systemSettings.auditTrail.mfaVerified',
  CHANGES_AND_EXPORTS_ONLY: 'systemSettings.auditTrail.changesAndExportsOnly',
};
